import type { ActionKind, RoundEvent, Stance as CombatStance } from '@cs-shot/battle-sim';
import type { Scene } from './types';
import type { RoundUpdate } from '../game/useRushMidMatch';
import { ENTER_DUR, GREN_FLIGHT, KNIFE_LUNGE, ROUND_ANIM, playTime } from './battleConfig';
import { buildFighters, syncTo, type PlaybackState } from './playbackState';
import type { Actors } from './actors';

export const buildRoundTargets = (events: RoundEvent[]): Map<string, string> => {
  const m = new Map<string, string>();
  const put = (shooter: string, target: string) => { if (!m.has(shooter)) m.set(shooter, target); };
  for (const e of events) {
    switch (e.type) {
      case 'shot': case 'knife': case 'grenade': case 'smoke': put(e.shooter, e.target); break;
      case 'flash': if (e.targets.length) put(e.shooter, e.targets[0]); break;
    }
  }
  return m;
};

export const findMutualKnifers = (events: RoundEvent[]): Set<string> => {
  const knifeBy = new Map<string, string>();
  for (const e of events) if (e.type === 'knife') knifeBy.set(e.shooter, e.target);
  const out = new Set<string>();
  for (const [shooter, target] of knifeBy) if (knifeBy.get(target) === shooter) out.add(shooter);
  return out;
};

export const buildRoundActions = (events: RoundEvent[]): Map<string, ActionKind> => {
  const m = new Map<string, ActionKind>();
  for (const e of events) {
    switch (e.type) {
      case 'shot': if (!m.has(e.shooter)) m.set(e.shooter, 'shoot'); break;
      case 'grenade': m.set(e.shooter, 'grenade'); break;
      case 'smoke': m.set(e.shooter, 'smoke'); break;
      case 'flash': m.set(e.shooter, 'flash'); break;
      case 'knife': m.set(e.shooter, 'knife'); break;
      case 'heal': m.set(e.id, 'medkit'); break;
    }
  }
  return m;
};

export const computeKnifeDeathTimes = (events: RoundEvent[], n: number): Map<string, number> => {
  const m = new Map<string, number>();
  for (const e of events) {
    if (e.type !== 'knife') continue;
    const apex = n + playTime(e.t) + KNIFE_LUNGE / 2;
    m.set(`${e.target}@${e.t}`, apex);
    m.set(`${e.shooter}@${e.t}`, apex);
  }
  return m;
};

const ENTRANCE_STANCES: CombatStance[] = ['attack', 'maneuver', 'defense'];
const entranceStance = (id: string): CombatStance => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ENTRANCE_STANCES[Math.abs(h) % ENTRANCE_STANCES.length];
};

export const scheduleRound = (sc: Scene, state: PlaybackState, actors: Actors, u: RoundUpdate, n: number): void => {
  const firstBuild = !sc.fighters.length;
  const snap = Object.fromEntries(u.fighters.map((f) => [f.id, { ...f }]));
  const startSnap = u.initFighters
    ? Object.fromEntries(u.initFighters.map((f) => [f.id, { ...f }]))
    : snap;
  if (firstBuild) buildFighters(sc, state.inits, startSnap);

  if (firstBuild && u.round > 0) {
    syncTo(sc, snap);
    for (const f of sc.fighters) f.x = f.targetX;
    state.cursor = Object.fromEntries(Object.entries(snap).map(([id, f]) => [id, { ...f }]));
    state.prevFinal = snap;
    state.timeline = [];
    state.pendingBlasts.length = 0;
    state.pendingSmokes.length = 0;
    state.pendingShots.length = 0;
    state.roundTargets = new Map();
    state.mutualKnifers = new Set();
    state.replayDur = 0;
    state.activeAt = n;
    return;
  }
  if (firstBuild) state.enterAt = n;
  const base = firstBuild ? n + ENTER_DUR : n;
  if (state.prevFinal) syncTo(sc, state.prevFinal);
  state.cursor = Object.fromEntries(
    Object.entries(state.prevFinal ?? startSnap).map(([id, f]) => [id, { ...f }]),
  );
  state.prevFinal = snap;
  const eventActions = buildRoundActions(u.events);
  const serverActions = u.actions;
  const applyRoundStances = () => {
    for (const f of sc.fighters) {
      const s = snap[f.id];
      if (s) f.combatStance = s.stance;
      const server = serverActions?.[f.id];
      if (server) {
        f.roundAction = server.kind;
        if (server.kind === 'shoot' && server.stance) f.combatStance = server.stance;
      } else if (serverActions) {
        f.roundAction = eventActions.get(f.id) ?? null;
      } else {
        const aliveAtStart = state.cursor?.[f.id]?.alive ?? false;
        f.roundAction = eventActions.get(f.id) ?? (aliveAtStart ? 'shoot' : null);
      }
      f.engagedId = null;
    }
  };

  state.timeline = [];
  state.pendingBlasts.length = 0;
  state.pendingSmokes.length = 0;
  state.pendingShots.length = 0;
  if (firstBuild) {
    for (const f of sc.fighters) { f.combatStance = entranceStance(f.id); f.roundAction = null; }
    state.timeline.push({ at: base, run: applyRoundStances });
  } else {
    applyRoundStances();
  }
  const events = u.events;
  state.roundTargets = buildRoundTargets(events);
  state.mutualKnifers = findMutualKnifers(events);
  const knifeDeathAt = computeKnifeDeathTimes(events, base);

  for (const s of state.smokeScreens.values()) {
    if (s.until < u.round && s.fadeAt == null) s.fadeAt = base;
  }

  for (const g of state.blindGlows.values()) {
    if (g.until < u.round && g.fadeAt == null) g.fadeAt = base;
  }
  for (const ev of events) {
    if (ev.type !== 'smoke') continue;
    const tg = sc.byId[ev.target];
    const prev = state.smokeScreens.get(ev.target);
    state.smokeScreens.set(ev.target, {
      until: u.round + 1,
      x: tg ? tg.spawnX : sc.W / 2,
      y: tg ? tg.y : sc.H / 2,
      ...(prev && prev.fadeAt == null && prev.bornAt != null ? { bornAt: prev.bornAt } : {}),
    });
  }

  for (const ev of events) {
    if (ev.type === 'shot') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyShot(ev) });
    } else if (ev.type === 'knife') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyKnife(ev) });
    } else if (ev.type === 'smoke') {
      const throwAt = base + playTime(ev.t);
      const land = throwAt + GREN_FLIGHT;
      state.timeline.push({ at: throwAt, run: () => actors.applySmokeThrow(ev, land) });
    } else if (ev.type === 'death') {
      const at = knifeDeathAt.get(`${ev.id}@${ev.t}`) ?? base + playTime(ev.t);
      state.timeline.push({ at, run: (nn) => actors.applyDeath(ev, nn) });
    } else if (ev.type === 'heal') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyHeal(ev) });
    } else if (ev.type === 'shield') {
      state.timeline.push({ at: base + playTime(ev.t), run: () => actors.applyShield(ev) });
    } else if (ev.type === 'flash') {
      const throwAt = base + playTime(ev.t);
      const land = throwAt + GREN_FLIGHT;
      state.timeline.push({ at: throwAt, run: () => actors.applyFlashThrow(ev, land) });
      state.timeline.push({ at: land, run: () => actors.applyFlashLand(ev, u.round) });
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closed union at compile time; guards RUNTIME events a NEWER server may send
    } else if (ev.type === 'grenade') {
      const land = base + playTime(ev.t);
      const throwAt = Math.max(base, land - GREN_FLIGHT);
      const tg = sc.byId[ev.target];
      const cx = tg ? tg.spawnX : sc.W / 2;
      const cy = tg ? tg.y - 80 : sc.H / 2;
      state.timeline.push({ at: throwAt, run: () => actors.applyGrenadeThrow(ev, land, cx, cy) });
      state.pendingBlasts.push({ at: land, cx, cy, ev });
    }
  // WHY: неизвестный тип события от более нового сервера пропускаем, не падаем — старый клиент обязан переживать новые
  // события (§4)
  }
  state.timeline.sort((a, b) => a.at - b.at);

  const maxT = events.length ? Math.min(ROUND_ANIM, Math.max(...events.map((e) => playTime(e.t)))) : 0;
  state.replayDur = events.length ? maxT + 0.4 : 0;
  state.activeAt = base;
};
